import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ItemEditor from '../components/ItemEditor';

interface Customer {
  id: number;
  name: string;
  honorific: string;
}

interface EstimateItem {
  id?: number;
  item_name: string;
  specification: string;
  quantity: number;
  unit: string;
  unit_price: number;
  amount: number;
  notes: string;
  sort_order: number;
}

interface EstimateForm {
  customer_id: number;
  project_name: string;
  site_address: string;
  issue_date: string;
  valid_until: string;
  delivery_date: string;
  payment_terms: string;
  discount: number;
  tax_rate: number;
  tax_rounding: 'floor' | 'round' | 'ceil';
  remarks: string;
  status: string;
}

const EstimateEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const [form, setForm] = useState<EstimateForm>({
    customer_id: 0,
    project_name: '',
    site_address: '',
    issue_date: new Date().toISOString().split('T')[0],
    valid_until: '',
    delivery_date: '',
    payment_terms: '',
    discount: 0,
    tax_rate: 10,
    tax_rounding: 'floor',
    remarks: '',
    status: 'draft',
  });

  const [items, setItems] = useState<EstimateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 顧客一覧取得
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const result = await window.api.customer.list({});
        setCustomers(result);
      } catch (err: any) {
        console.error('顧客取得エラー:', err);
      }
    };
    fetchCustomers();
  }, []);

  // 既存データ読み込み
  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }

    const fetchEstimate = async () => {
      try {
        const result = await window.api.estimate.get(Number(id));
        setForm({
          customer_id: result.customer_id,
          project_name: result.project_name,
          site_address: result.site_address,
          issue_date: result.issue_date,
          valid_until: result.valid_until,
          delivery_date: result.delivery_date,
          payment_terms: result.payment_terms,
          discount: result.discount,
          tax_rate: result.tax_rate,
          tax_rounding: result.tax_rounding,
          remarks: result.remarks,
          status: result.status,
        });
        setItems(result.items || []);
        if (result.customer_id) {
          const customer = customers.find(c => c.id === result.customer_id);
          if (customer) {
            setCustomerSearch(customer.name);
          }
        }
      } catch (err: any) {
        setError(err.message || 'データ取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchEstimate();
  }, [id, isNew, customers]);

  // 自動計算
  const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const taxableAmount = subtotal - form.discount;
  let taxAmount: number;
  const taxBase = taxableAmount * (form.tax_rate / 100);
  switch (form.tax_rounding) {
    case 'floor':
      taxAmount = Math.floor(taxBase);
      break;
    case 'ceil':
      taxAmount = Math.ceil(taxBase);
      break;
    case 'round':
    default:
      taxAmount = Math.round(taxBase);
      break;
  }
  const total = taxableAmount + taxAmount;

  // 顧客選択
  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const handleCustomerSelect = (customer: Customer) => {
    setForm(prev => ({ ...prev, customer_id: customer.id }));
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  // 保存
  const handleSave = async (status: string = 'draft') => {
    setSaving(true);
    setError(null);
    try {
      const data = {
        ...form,
        status,
        items: items.map((item, index) => ({
          ...item,
          sort_order: index,
        })),
      };

      let resultId: number;
      if (isNew) {
        resultId = await window.api.estimate.create(data);
      } else {
        await window.api.estimate.update(Number(id), data);
        resultId = Number(id);
      }

      navigate('/estimate');
    } catch (err: any) {
      setError(err.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 発行
  const handleIssue = async () => {
    if (!form.customer_id) {
      alert('顧客を選択してください');
      return;
    }
    if (!form.project_name) {
      alert('件名を入力してください');
      return;
    }
    await handleSave('issued');
  };

  // PDFプレビュー
  const handlePdfPreview = async () => {
    try {
      const targetId = isNew ? null : Number(id);
      if (!targetId) {
        alert('先に保存してください');
        return;
      }
      const pdfPath = await window.api.estimate.generatePdf(targetId);
      alert(`PDFを出力しました: ${pdfPath}`);
    } catch (err: any) {
      alert(err.message || 'PDF出力に失敗しました');
    }
  };

  // 複製
  const handleDuplicate = async () => {
    if (!id || isNew) return;
    try {
      const newId = await window.api.estimate.duplicate(Number(id));
      navigate(`/estimate/${newId}`);
    } catch (err: any) {
      alert(err.message || '複製に失敗しました');
    }
  };

  // 削除
  const handleDelete = async () => {
    if (!id || isNew) return;
    if (!window.confirm('この見積を削除してもよろしいですか？')) return;
    try {
      await window.api.estimate.delete(Number(id));
      navigate('/estimate');
    } catch (err: any) {
      alert(err.message || '削除に失敗しました');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
        読み込み中...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', color: '#333', marginBottom: '20px' }}>
        {isNew ? '新規見積作成' : '見積編集'}
      </h1>

      {error && (
        <div style={{
          padding: '10px 15px',
          backgroundColor: '#f2dede',
          color: '#a94442',
          borderRadius: '4px',
          marginBottom: '15px',
        }}>
          {error}
        </div>
      )}

      {/* 顧客選択 */}
      <div style={{ marginBottom: '15px', position: 'relative' }}>
        <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>顧客</label>
        <input
          type="text"
          value={customerSearch}
          onChange={(e) => {
            setCustomerSearch(e.target.value);
            setShowCustomerDropdown(true);
            setForm(prev => ({ ...prev, customer_id: 0 }));
          }}
          onFocus={() => setShowCustomerDropdown(true)}
          placeholder="顧客名を入力..."
          style={{
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        />
        {showCustomerDropdown && filteredCustomers.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            {filteredCustomers.map(customer => (
              <div
                key={customer.id}
                onClick={() => handleCustomerSelect(customer)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
              >
                {customer.name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* フォーム */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>件名</label>
          <input
            type="text"
            value={form.project_name}
            onChange={(e) => setForm(prev => ({ ...prev, project_name: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>工事場所</label>
          <input
            type="text"
            value={form.site_address}
            onChange={(e) => setForm(prev => ({ ...prev, site_address: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>発行日</label>
          <input
            type="date"
            value={form.issue_date}
            onChange={(e) => setForm(prev => ({ ...prev, issue_date: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>有効期限</label>
          <input
            type="date"
            value={form.valid_until}
            onChange={(e) => setForm(prev => ({ ...prev, valid_until: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>納期</label>
          <input
            type="date"
            value={form.delivery_date}
            onChange={(e) => setForm(prev => ({ ...prev, delivery_date: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>支払条件</label>
          <input
            type="text"
            value={form.payment_terms}
            onChange={(e) => setForm(prev => ({ ...prev, payment_terms: e.target.value }))}
            style={inputStyle}
          />
        </div>
      </div>

      {/* 明細エディタ */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ fontSize: '16px', color: '#333', marginBottom: '10px' }}>明細</h3>
        <ItemEditor items={items} onChange={setItems} />
      </div>

      {/* 値引・税率・端数処理 */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>値引</label>
          <input
            type="number"
            value={form.discount}
            onChange={(e) => setForm(prev => ({ ...prev, discount: Number(e.target.value) || 0 }))}
            style={{ ...inputStyle, width: '120px', textAlign: 'right' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>税率</label>
          <select
            value={form.tax_rate}
            onChange={(e) => setForm(prev => ({ ...prev, tax_rate: Number(e.target.value) }))}
            style={{ ...inputStyle, width: '80px' }}
          >
            <option value={10}>10%</option>
            <option value={8}>8%</option>
            <option value={0}>0%</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>税端数処理</label>
          <select
            value={form.tax_rounding}
            onChange={(e) => setForm(prev => ({ ...prev, tax_rounding: e.target.value as any }))}
            style={{ ...inputStyle, width: '100px' }}
          >
            <option value="floor">切捨</option>
            <option value="round">四捨五入</option>
            <option value="ceil">切上</option>
          </select>
        </div>
      </div>

      {/* 自動計算表示 */}
      <div style={{
        backgroundColor: '#f9f9f9',
        padding: '15px',
        borderRadius: '4px',
        marginBottom: '20px',
        textAlign: 'right',
      }}>
        <div style={{ marginBottom: '5px' }}>
          <span style={{ fontSize: '13px', color: '#666', marginRight: '20px' }}>小計</span>
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>¥{subtotal.toLocaleString()}</span>
        </div>
        <div style={{ marginBottom: '5px' }}>
          <span style={{ fontSize: '13px', color: '#666', marginRight: '20px' }}>値引</span>
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>-¥{form.discount.toLocaleString()}</span>
        </div>
        <div style={{ marginBottom: '5px' }}>
          <span style={{ fontSize: '13px', color: '#666', marginRight: '20px' }}>消費税({form.tax_rate}%)</span>
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>¥{taxAmount.toLocaleString()}</span>
        </div>
        <div style={{ borderTop: '2px solid #333', paddingTop: '5px' }}>
          <span style={{ fontSize: '16px', color: '#337ab7', marginRight: '20px', fontWeight: 'bold' }}>合計(税込)</span>
          <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#337ab7' }}>¥{total.toLocaleString()}</span>
        </div>
      </div>

      {/* 備考 */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '4px' }}>備考</label>
        <textarea
          value={form.remarks}
          onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))}
          rows={4}
          style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
        />
      </div>

      {/* ボタン */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => navigate('/estimate')}
          style={buttonStyle}
        >
          戻る
        </button>
        <button
          onClick={() => handleSave('draft')}
          disabled={saving}
          style={{ ...buttonStyle, backgroundColor: '#f0ad4e', color: 'white' }}
        >
          {saving ? '保存中...' : '保存(下書き)'}
        </button>
        <button
          onClick={handleIssue}
          disabled={saving}
          style={{ ...buttonStyle, backgroundColor: '#5bc0de', color: 'white' }}
        >
          発行
        </button>
        {!isNew && (
          <>
            <button
              onClick={handlePdfPreview}
              style={{ ...buttonStyle, backgroundColor: '#5cb85c', color: 'white' }}
            >
              PDFプレビュー
            </button>
            <button
              onClick={handleDuplicate}
              style={{ ...buttonStyle, backgroundColor: '#337ab7', color: 'white' }}
            >
              複製
            </button>
            <button
              onClick={handleDelete}
              style={{ ...buttonStyle, backgroundColor: '#d9534f', color: 'white' }}
            >
              削除
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '13px',
  width: '100%',
  boxSizing: 'border-box',
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 'bold',
};

export default EstimateEdit;
